/**
 * TemplateSelector Component
 * Modal for selecting a requirements document template
 */

import {
  X,
  FileText,
  Zap,
  Users,
  Sparkles,
} from 'lucide-react';

export interface TemplateSection {
  type: string;
  title: string;
  required?: boolean;
}

export interface RequirementsTemplate {
  id: string;
  name: string;
  description: string;
  icon: 'FileText' | 'Zap' | 'Users' | 'Sparkles';
  sections: TemplateSection[];
  aiGuided?: boolean;
}

// Template definitions
export const TEMPLATES: RequirementsTemplate[] = [
  {
    id: 'prd-full',
    name: 'Full PRD',
    description: 'Complete Product Requirements Document with all sections',
    icon: 'FileText',
    sections: [
      { type: 'overview', title: 'Executive Summary', required: true },
      { type: 'problem_statement', title: 'Problem Statement', required: true },
      { type: 'user_needs', title: 'User Needs', required: true },
      { type: 'functional_requirements', title: 'Functional Requirements', required: true },
      { type: 'non_functional_requirements', title: 'Non-Functional Requirements', required: false },
      { type: 'acceptance_criteria', title: 'Acceptance Criteria', required: true },
      { type: 'constraints', title: 'Constraints & Assumptions', required: false },
      { type: 'dependencies', title: 'Dependencies', required: false },
    ],
  },
  {
    id: 'feature-brief',
    name: 'Feature Brief',
    description: 'Lightweight requirements for a single feature',
    icon: 'Zap',
    sections: [
      { type: 'overview', title: 'Overview', required: true },
      { type: 'user_needs', title: 'User Need', required: true },
      { type: 'functional_requirements', title: 'Requirements', required: true },
      { type: 'acceptance_criteria', title: 'Acceptance Criteria', required: true },
    ],
  },
  {
    id: 'user-stories',
    name: 'User Story Collection',
    description: 'Multiple user stories with acceptance criteria',
    icon: 'Users',
    sections: [
      { type: 'overview', title: 'Epic Overview', required: true },
      { type: 'user_stories', title: 'User Stories', required: true },
      { type: 'acceptance_criteria', title: 'Acceptance Criteria', required: true },
    ],
  },
  {
    id: 'blank-guided',
    name: 'Blank + AI Guide',
    description: 'Start empty, Martha guides you section by section',
    icon: 'Sparkles',
    sections: [],
    aiGuided: true,
  },
];

const IconMap = {
  FileText: FileText,
  Zap: Zap,
  Users: Users,
  Sparkles: Sparkles,
};

interface TemplateSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (template: RequirementsTemplate) => void;
}

export function TemplateSelector({ isOpen, onClose, onSelect }: TemplateSelectorProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="template-selector-title"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <div>
            <h2 id="template-selector-title" className="text-xl font-bold text-stone-900">
              Choose a Template
            </h2>
            <p className="text-sm text-stone-500 mt-1">
              Select a starting point for your requirements document
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Template Grid */}
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4">
            {TEMPLATES.map((template) => {
              const Icon = IconMap[template.icon];
              return (
                <button
                  key={template.id}
                  onClick={() => onSelect(template)}
                  className="flex flex-col items-start p-5 text-left bg-stone-50 hover:bg-coral-50
                           border-2 border-transparent hover:border-coral-300 rounded-xl
                           transition-all duration-200 group"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-lg bg-white group-hover:bg-coral-100 transition-colors">
                      <Icon className="w-5 h-5 text-coral-500" />
                    </div>
                    <h3 className="font-semibold text-stone-900 group-hover:text-coral-700 transition-colors">
                      {template.name}
                    </h3>
                  </div>
                  <p className="text-sm text-stone-500 mb-4">
                    {template.description}
                  </p>

                  {/* Section preview */}
                  {template.sections.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {template.sections.slice(0, 4).map((section) => (
                        <span
                          key={section.type}
                          className="px-2 py-0.5 text-xs bg-white text-stone-500 rounded border border-stone-200"
                        >
                          {section.title}
                        </span>
                      ))}
                      {template.sections.length > 4 && (
                        <span className="px-2 py-0.5 text-xs text-stone-400">
                          +{template.sections.length - 4} more
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-xs text-purple-600">
                      <Sparkles className="w-3 h-3" />
                      <span>AI-guided experience</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-stone-50 border-t border-stone-100">
          <p className="text-xs text-stone-400 text-center">
            You can customize any template after creation. Martha will help you fill in the sections.
          </p>
        </div>
      </div>
    </div>
  );
}
